import { useMemo, useState, useCallback } from 'react';
import { usePricing, PricingData } from './usePricing';
import {
  masterFeatures,
  pricingTiers as staticPricingTiers,
  PricingTier,
  getTierFeatureInfo,
  isFeatureIncluded
} from '@/lib/pricingData';

export type BillingInterval = 'month' | 'year';

/**
 * Hook that combines static tier configuration with dynamic pricing from database
 * Returns fully formed PricingTier objects with live pricing data
 * Supports both monthly and annual billing intervals
 */
export function useDynamicPricing(initialInterval: BillingInterval = 'year') {
  const { data: pricingData, isLoading, error } = usePricing();
  const [billingInterval, setBillingInterval] = useState<BillingInterval>(initialInterval);

  // Create pricing lookup map for quick access
  const pricingMap = useMemo(() => {
    const map = new Map<string, PricingData>();
    if (pricingData && pricingData.length > 0) {
      pricingData.forEach(pricing => {
        const key = `${pricing.tier_name}-${pricing.interval_type}`;
        map.set(key, pricing);
      });
    }
    return map;
  }, [pricingData]);

  const pricingTiers = useMemo(() => {
    // If we have dynamic pricing data, merge it with static configs
    if (pricingData && pricingData.length > 0) {
      // Return static tiers with dynamic pricing where available
      return staticPricingTiers.map(tier => {
        if (tier.name === 'Free') {
          return tier; // Free tier always has static $0 pricing
        }

        // Look for dynamic pricing for this tier at the selected interval
        const dynamicPricing = pricingMap.get(`${tier.name}-${billingInterval}`);
        if (dynamicPricing) {
          return {
            ...tier,
            price: dynamicPricing.formatted_price || tier.price,
            period: billingInterval
          };
        }

        return tier; // Use static price if no dynamic pricing found
      });
    }

    // Fall back to static pricing tiers from pricingData.ts
    return staticPricingTiers;
  }, [pricingData, pricingMap, billingInterval]);

  // Get pricing for a specific tier at both intervals (for savings calculation)
  const getTierPricing = useCallback((tierName: string) => {
    const monthlyPricing = pricingMap.get(`${tierName}-month`);
    const yearlyPricing = pricingMap.get(`${tierName}-year`);

    const monthlyPrice = monthlyPricing?.display_price_cents || 2000; // $20 fallback
    const yearlyPrice = yearlyPricing?.display_price_cents || 20000; // $200 fallback

    // Calculate savings: (12 * monthly) - yearly
    const yearlyEquivalent = monthlyPrice * 12;
    const savingsAmount = yearlyEquivalent - yearlyPrice;
    const savingsPercent = Math.round((savingsAmount / yearlyEquivalent) * 100);

    // Calculate free months: how many months equivalent the savings represent
    const freeMonths = monthlyPrice > 0 ? Math.round(savingsAmount / monthlyPrice) : 0;

    return {
      monthly: {
        cents: monthlyPrice,
        formatted: monthlyPricing?.formatted_price || '$20'
      },
      yearly: {
        cents: yearlyPrice,
        formatted: yearlyPricing?.formatted_price || '$200'
      },
      savings: {
        cents: savingsAmount,
        formatted: `$${Math.round(savingsAmount / 100)}`,
        percent: savingsPercent,
        freeMonths
      }
    };
  }, [pricingMap]);

  // Check if annual pricing is available
  const hasAnnualPricing = useMemo(() => {
    return pricingMap.has('Unlimited-year');
  }, [pricingMap]);

  return {
    pricingTiers,
    masterFeatures,
    isLoading,
    error,
    // Billing interval state
    billingInterval,
    setBillingInterval,
    hasAnnualPricing,
    // Pricing helpers
    getTierPricing,
    // Helper functions (re-exported for convenience)
    getTierFeatureInfo: (tier: PricingTier, featureId: string) => getTierFeatureInfo(tier, featureId),
    isFeatureIncluded: (tier: PricingTier, featureId: string) => isFeatureIncluded(tier, featureId),
    // Raw pricing data for advanced use cases
    rawPricingData: pricingData
  };
}

/**
 * Hook to get pricing for a specific tier by name
 */
export function useTierByName(tierName: string, billingInterval: BillingInterval = 'month') {
  const { pricingTiers, isLoading, error } = useDynamicPricing(billingInterval);

  const tier = useMemo(() => {
    return pricingTiers.find(t => t.name.toLowerCase() === tierName.toLowerCase());
  }, [pricingTiers, tierName]);

  return {
    tier,
    isLoading,
    error,
    exists: !!tier
  };
}
